
import React, { useState, useEffect } from 'react';
import { Edge } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { ArrowRight, CornerRightDown, ArrowLeft } from 'lucide-react';

interface EdgePropertiesPanelProps {
  edge: Edge | null;
  onClose: () => void;
  onSave: (edgeId: string, type: string, label: string, markerStart: boolean, markerEnd: boolean, color: string, lineStyle: string) => void;
}

export function EdgePropertiesPanel({ 
  edge, 
  onClose, 
  onSave 
}: EdgePropertiesPanelProps) {
  const [edgeType, setEdgeType] = useState<string>('default');
  const [edgeLabel, setEdgeLabel] = useState<string>('');
  const [markerStart, setMarkerStart] = useState<boolean>(false);
  const [markerEnd, setMarkerEnd] = useState<boolean>(false);
  const [edgeColor, setEdgeColor] = useState<string>('#6b7280');
  const [lineStyle, setLineStyle] = useState<string>('solid');

  const colorOptions = [
    { value: '#6b7280', label: 'Cinza', color: '#6b7280' },
    { value: '#3b82f6', label: 'Azul', color: '#3b82f6' },
    { value: '#10b981', label: 'Verde', color: '#10b981' },
    { value: '#f59e0b', label: 'Amarelo', color: '#f59e0b' },
    { value: '#ef4444', label: 'Vermelho', color: '#ef4444' },
    { value: '#8b5cf6', label: 'Roxo', color: '#8b5cf6' },
    { value: '#ec4899', label: 'Rosa', color: '#ec4899' }
  ];

  const lineStyleOptions = [
    { value: 'solid', label: 'Contínuo' },
    { value: 'dashed', label: 'Tracejado' },
    { value: 'dotted', label: 'Pontilhado' }
  ];

  useEffect(() => {
    if (edge) {
      setEdgeType(edge.type || 'default');
      setEdgeLabel(edge.label as string || '');
      setMarkerStart(!!edge.markerStart);
      setMarkerEnd(!!edge.markerEnd);
      setEdgeColor(edge.style?.stroke || '#6b7280');
      setLineStyle(edge.style?.strokeDasharray ? (edge.style.strokeDasharray === '5,5' ? 'dashed' : 'dotted') : 'solid');
    }
  }, [edge]);

  if (!edge) return null;

  const handleSave = () => {
    onSave(edge.id, edgeType, edgeLabel, markerStart, markerEnd, edgeColor, lineStyle);
    onClose();
  };

  const handleMarkerStartChange = (checked: boolean | "indeterminate") => {
    setMarkerStart(checked === true);
  };

  const handleMarkerEndChange = (checked: boolean | "indeterminate") => {
    setMarkerEnd(checked === true);
  };

  return (
    <Sheet open={!!edge} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px]" showOverlay={false}>
        <SheetHeader>
          <SheetTitle>Propriedades da Conexão</SheetTitle>
        </SheetHeader>
        
        <div className="py-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="edge-label" className="text-base">Título da Conexão</Label>
            <Input
              id="edge-label"
              value={edgeLabel}
              onChange={(e) => setEdgeLabel(e.target.value)}
              placeholder="Digite um título para a conexão"
            />
          </div>

          <div className="space-y-4">
            <Label className="text-base">Estilo da Linha</Label>
            <RadioGroup 
              value={edgeType} 
              onValueChange={setEdgeType}
              className="grid grid-cols-2 gap-4"
            >
              <div className={`flex flex-col items-center space-y-2 border rounded-md p-4 hover:bg-accent transition-colors cursor-pointer ${
                edgeType === 'default' ? 'bg-primary/10 border-primary text-primary' : ''
              }`}>
                <RadioGroupItem value="default" id="line-default" className="sr-only" />
                <Label htmlFor="line-default" className="cursor-pointer flex flex-col items-center">
                  <ArrowRight size={24} className="mb-2" />
                  <span className="font-medium">Reta</span>
                </Label>
              </div>
              
              <div className={`flex flex-col items-center space-y-2 border rounded-md p-4 hover:bg-accent transition-colors cursor-pointer ${
                edgeType === 'step' ? 'bg-primary/10 border-primary text-primary' : ''
              }`}>
                <RadioGroupItem value="step" id="line-step" className="sr-only" />
                <Label htmlFor="line-step" className="cursor-pointer flex flex-col items-center">
                  <CornerRightDown size={24} className="mb-2" />
                  <span className="font-medium">Ortogonal</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <Label className="text-base">Setas da Conexão</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="marker-start" 
                  checked={markerStart}
                  onCheckedChange={handleMarkerStartChange}
                />
                <Label htmlFor="marker-start" className="flex items-center space-x-2 cursor-pointer">
                  <ArrowLeft size={16} />
                  <span>Seta na Origem</span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="marker-end" 
                  checked={markerEnd}
                  onCheckedChange={handleMarkerEndChange}
                />
                <Label htmlFor="marker-end" className="flex items-center space-x-2 cursor-pointer">
                  <ArrowRight size={16} />
                  <span>Seta no Destino</span>
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cor da Linha</Label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setEdgeColor(option.value)}
                  className={`w-8 h-8 border rounded transition-all ${
                    edgeColor === option.value 
                      ? 'border-[#05445e] border-2' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{
                    backgroundColor: option.color
                  }}
                  title={option.label}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Estilo da Linha</Label>
            <div className="grid grid-cols-3 gap-2">
              {lineStyleOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setLineStyle(option.value)}
                  className={`p-3 border rounded-lg transition-all flex items-center justify-center ${
                    lineStyle === option.value 
                      ? 'border-[#05445e] border-2 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <svg width="40" height="4" viewBox="0 0 40 4">
                      <line
                        x1="0"
                        y1="2"
                        x2="40"
                        y2="2"
                        stroke={edgeColor}
                        strokeWidth="2"
                        strokeDasharray={
                          option.value === 'dashed' ? '5,5' :
                          option.value === 'dotted' ? '2,2' : 'none'
                        }
                      />
                    </svg>
                    <span className="text-xs">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <SheetFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={handleSave}
            className="bg-[#189ab4] hover:bg-[#05445e]"
          >
            Salvar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
